import { pdf } from "@react-pdf/renderer";
import { t } from "i18next";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import CustomerService from "../../api/CustomerService";
import DeficiencyService from "../../api/DeficiencyService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { Comments } from "../../components/Comments/Comments";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomCollapseWithChildren } from "../../components/CustomCollapseWithChildren/CustomCollapseWithChildren";
import { CustomInput } from "../../components/CustomInput/CustomInput";
import { CustomTextArea } from "../../components/CustomTextArea/CustomTextArea";
import { DeficiencyDetails } from "../../components/DeficiencyDetails/DeficiencyDetails";
import { DeficiencyType } from "../../components/DeficiencyType/DeficiencyType";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import Modal from "../../components/Modal/Modal";
import { DeficiencyPDFDocumentForm } from "../../components/PDFDocument/DeficiencyPDFDocumentForm";
import PinCropMap from "../../components/PinCropMap/PinCropMap";
import { ResourceImages } from "../../components/ResourceImages/ResourceImages";
import { ShareDefectBox } from "../../components/ShareDefectBox/ShareDefectBox";
import { TabTypeSelection } from "../../components/TabTypeSelection/TabTypeSelection";
import { TechniciansList } from "../../components/TechniciansList/TechniciansList";
import useDeficiencyComments from "../../hooks/useDeficiencyComments";
import { useDocumentation } from "../../hooks/useDocumentation";
import { useDownloadFiles } from "../../hooks/useDownloadFiles";
import { useRedirect } from "../../hooks/useRedirect";
import { useService } from "../../hooks/useService";
import { useUnauthorizedAccess } from "../../hooks/useUnauthorizedAccess";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { RootState } from "../../store";
import { checkIsRunning } from "../../store/clock-slice";
import { deficiencyServiceActions } from "../../store/deficiency-service-slice";
import { creatingDocumentationActions } from "../../store/documentation-slice";
import { overviewAppointmentSliceActions } from "../../store/overview-appointment-slice";
import { activeTabsActions } from "../../store/tabs-slice";
import styles from "../../styles/deficiency-insight.module.css";
import { Appointment } from "../../types/appointment";
import { Comment } from "../../types/comment";
import { Deficiency } from "../../types/deficiencies";
import { ImageProps } from "../../types/image";
import { Property, PropertyArea } from "../../types/property";
import { Technician } from "../../types/user";
import { getDeficiencyType } from "../../utils/getDeficiencyType";
import { parseAddress, parseAddressWithObject } from "../../utils/parseAddress";

type DeficiencyStateType = {
  data: Deficiency;
  includes?: {
    propertyArea?: PropertyArea[];
    appointments?: Appointment[];
    documents?: ImageProps[];
    comments?: Comment[];
    property?: Property[];
  };
};

const DeficiencyInsight = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { deficiencyId } = useParams();
  const deficiencyDateForDetails = location.state?.deficiencyDate || null;

  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareSuccessModalOpen, setShareSuccessModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [note, setNote] = useState("");
  const { setDashboardUrlAndRedirect } = useRedirect();

  const originalAppointment = useSelector(
    (state: RootState) => state.appointment
  );
  const appointment = useSelector(
    (state: RootState) => state.overviewAppointment
  );
  const isCustomerAdmin = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );
  const previousPath = useSelector(
    (state: RootState) => state.tabs.previousPath
  );
  const user = useSelector((state: RootState) => state.user.currentUser.user);
  const isRunning = useSelector((state: RootState) =>
    checkIsRunning(state, appointment.id, user!.id)
  );
  const isViewOnly = useSelector((state: RootState) => state.property.viewOnly);

  const { startService } = useService(appointment);

  const [deficiency, setDeficiency] = useState<DeficiencyStateType | null>(
    null
  );
  useUnauthorizedAccess(deficiency?.data);

  const { type, subType } = getDeficiencyType(
    deficiency?.data.deficiencyType,
    deficiency?.data.deficiencySubType
  );

  const activeTab =
    deficiency?.data.responsibility === "exterminator" ? "SBK" : "Kunde";

  const includeComments =
    deficiency?.data.responsibility === "customer" ? true : false;

  const { closeModal } = useDownloadFiles();

  const isEmpty = useSelector(
    (state: RootState) => state.technicianDocuments.empty
  );

  const isExterminatorResponsibility =
    deficiency?.data.responsibility === "exterminator";

  const selectedProperty = useSelector(
    (state: RootState) => state.user.selectedProperty
  );

  const documentation = useSelector((state: RootState) => state.documentation);
  const { comments, loadingComments, fetchDeficiencyComments, addComment } =
    useDeficiencyComments(deficiencyId);

  const { sendPDFviaEmail, requestLoading } = useDocumentation();

  const handleBackButtonClicked = () => {
    if (isCustomerAdmin) {
      dispatch(creatingDocumentationActions.resetState());
      dispatch(overviewAppointmentSliceActions.resetState());

      return setDashboardUrlAndRedirect("/deficiencies");
    } else if (previousPath === "/") {
      dispatch(activeTabsActions.setDashbaordUrl(""));
      navigate("/");
    } else if (isViewOnly) {
      navigate(
        `/properties/${deficiency?.includes?.property?.[0]?.id}/viewonly`
      );
    } else {
      navigate(`/properties/${originalAppointment.id}/show`);
    }
    dispatch(deficiencyServiceActions.resetState());
    dispatch(creatingDocumentationActions.resetState());
    dispatch(overviewAppointmentSliceActions.resetState());
  };

  useEffect(() => {
    if (!deficiencyId) {
      return;
    }
    setLoading(true);
    DeficiencyService.GetDeficiency(deficiencyId)
      .then(async (deficiency) => {
        setDeficiency(deficiency);

        let street = "";
        let postcode = "";
        let city = "";
        let objectName: string | null = null;

        if (isExterminatorResponsibility) {
          const parsed = parseAddress(
            deficiency?.includes?.appointments?.[0]?.additionalData?.property
              ?.address
          );

          street =
            parsed?.street || deficiency?.includes?.property?.[0]?.street || "";
          postcode =
            parsed?.postcode ||
            deficiency?.includes?.property?.[0]?.postcode ||
            "";
          city =
            parsed?.city || deficiency?.includes?.property?.[0]?.city || "";

          objectName =
            deficiency?.includes?.appointments?.[0]?.additionalData?.property
              ?.name ?? null;
        } else {
          const parsed = parseAddressWithObject(selectedProperty?.street);

          street = parsed?.street || "";
          objectName = parsed?.objectName || null;

          postcode = selectedProperty?.postcode || "";
          city = selectedProperty?.city || "";
        }

        let customerName = "";
        if (
          deficiency.data.customerId &&
          deficiency.data.responsibility === "customer"
        ) {
          try {
            const customerResponse = await CustomerService.GetCustomer(
              deficiency.data.customerId
            );
            customerName = customerResponse.data.firstName;
          } catch (error) {
            console.error("Failed to fetch customer:", error);
          }
        } else {
          customerName = appointment.additionalData.customer.name;
        }

        const appointmentSubsetData = {
          customerName:
            customerName || appointment.additionalData.customer.name,
          address: deficiency?.includes?.property?.[0]?.street ?? "",
          street: street,
          postcode,
          city,
          objectName,
          technicians,
          typeOfAppointment: null,
        };

        dispatch(
          creatingDocumentationActions.setAppointmentSubset(
            appointmentSubsetData
          )
        );

        fetchDeficiencyComments();

        if (deficiency?.includes?.appointments?.[0])
          dispatch(
            overviewAppointmentSliceActions.setAppointment(
              deficiency?.includes?.appointments?.[0]
            )
          );
      })
      .catch((error) => {
        console.error(error);
        navigate(-1);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    deficiencyId,
    dispatch,
    overviewAppointmentSliceActions.setAppointment,
    creatingDocumentationActions.setAppointmentSubset,
    fetchDeficiencyComments,
  ]);

  useEffect(() => {
    if (isCustomerAdmin && deficiency) {
      const deficiencyPDF = {
        deficiencyType: deficiency.data.deficiencyType,
        date: moment(deficiency.data.createdAt).format("DD. MMMM YYYY"),
        time: deficiency.data.createdAt,
        location: `${deficiency?.data.propertyArea?.[0]?.label || ""}, ${
          deficiency.data.location || ""
        }`,
        responsibility: deficiency.data.responsibility,
        requiredActions: deficiency.data.requiredActions,
        additionalText: deficiency.data.comment,
        state: deficiency.data.state,
        commentsList: comments,
        processedBy: deficiency.data.processedBy,
        processedAt: deficiency.data.changedAt,
      };

      dispatch(creatingDocumentationActions.setDeficiencyPDF(deficiencyPDF));
    }
  }, [comments, isCustomerAdmin, deficiency]);

  const deficiencyLocation = `${deficiency?.data.propertyArea[0].label}, ${deficiency?.data.location}`;

  const propertyAreaMap = useMemo(() => {
    const [propertyArea] = deficiency?.includes?.propertyArea || [];

    return propertyArea?.map;
  }, [deficiency?.includes?.propertyArea]);

  const technicians: Technician[] =
    deficiency?.includes?.appointments?.[0]?.technicians ?? [];

  const images = deficiency?.includes?.documents ?? [];

  const handleFixDeficiency = () => {
    if (isRunning) {
      setModalMessage("AnotherServiceInProgress");
    } else if (isCustomerAdmin) {
      setModalMessage("ModalEditCV");
      setNote("ModalEditCVNote");
    } else {
      setModalMessage("FixMangel");
      setNote("WorkingHoursBackgroundCount");
    }
    setIsModalOpen(true);
  };

  const handleStartServiceOnMangel = () => {
    if (deficiency?.data) {
      dispatch(deficiencyServiceActions.setDeficiency(deficiency.data));
      if (images) dispatch(deficiencyServiceActions.setExistingImages(images));
      setIsModalOpen(false);

      if (isCustomerAdmin) {
        dispatch(deficiencyServiceActions.setIsCustomerEditing(true));
      } else {
        startService();
      }

      const url = `/deficiencies/${deficiencyId}/resolve`;
      setDashboardUrlAndRedirect(url);
    }
  };

  const attachPhotos = documentation.showPhotosInPDF ? images : [];
  const property = deficiency?.includes?.property?.[0];
  const objectName = documentation.appointmentSubset.objectName;
  const emailSubject = `geteilter Mangel für ${
    objectName ? `${objectName}, ` : ""
  }${property?.street ?? ""} (${deficiency?.data.propertyArea[0].label}, ${
    deficiency?.data.location
  }), ${property?.postcode ?? ""} ${property?.city ?? ""}`;

  const shareMangel = async () => {
    setTimeout(async () => {
      const docInstance = (
        <DeficiencyPDFDocumentForm documentation={documentation} />
      );
      const blob = await pdf(docInstance).toBlob();
      if (deficiencyId && blob)
        sendPDFviaEmail(
          deficiencyId,
          blob,
          documentation.emails,
          emailSubject,
          attachPhotos
        );
    }, 100);

    setShareModalOpen(false);
    setShareSuccessModalOpen(true);
    dispatch(creatingDocumentationActions.resetEmails());
  };

  const handleCloseModal = () => {
    setShareModalOpen(false);

    dispatch(creatingDocumentationActions.resetEmails());
  };

  const monitorPinAbbr = useMemo(
    () => ({
      name: type?.slice(0, 3).toUpperCase(),
      number: "001",
    }),
    [type]
  );

  return (
    <AppLayout hideHeader={!isCustomerAdmin}>
      <Modal
        isOpen={isModalOpen}
        message={t(modalMessage)}
        onConfirm={() => setIsModalOpen(false)}
        {...(!isRunning && {
          note: t(note) ?? "",
          onCancel: () => setIsModalOpen(false),
          onConfirm: handleStartServiceOnMangel,
        })}
        {...(isCustomerAdmin && {
          confirmText: t("FixDeficiency") ?? "",
          deleteIconColor: "dark",
        })}
      />

      <Modal
        isOpen={shareSuccessModalOpen}
        message={t("SharedDefectSuccess")}
        note={"SharedDefectSuccessSubtitle"}
        additionalText={t("SharedDefectSuccessNote")}
        noteColor="#8493AB"
        onConfirm={() => {
          setShareSuccessModalOpen(false);
        }}
      />

      <Modal
        isOpen={isEmpty}
        message={t("NoInformation")}
        note={"NoAvailableDocuments"}
        iconType="noInfo"
        confirmText={"Back"}
        onConfirm={closeModal}
      />

      <ShareDefectBox
        isOpen={shareModalOpen}
        onCancel={handleCloseModal}
        onConfirm={shareMangel}
        emails={documentation.emails}
        showCommentsCheckbox={includeComments}
        loading={requestLoading}
      />

      {isCustomerAdmin ? (
        <BackButtonHeader
          handleBackButtonClicked={handleBackButtonClicked}
          showDocumentation={false}
          shareDefect
          icon="left"
          text={t("Open")}
          handleShareDefect={() => setShareModalOpen(true)}
        />
      ) : (
        <BackButtonHeader
          handleBackButtonClicked={handleBackButtonClicked}
          showDocumentation={false}
        />
      )}

      {isCustomerAdmin ? (
        <DeficiencyDetails
          deficiencyType={type}
          deficiencySubType={subType}
          date={deficiency?.data.createdAt}
          propertyArea={deficiency?.data.propertyArea[0].label ?? ""}
          location={deficiency?.data.location ?? ""}
          responsibility={deficiency?.data.responsibility ?? ""}
        />
      ) : (
        <AppointmentDetails
          deficiency
          showMangelTag={!isExterminatorResponsibility}
          deficiencyDate={deficiencyDateForDetails}
        />
      )}

      {loading && <LoadingSpinner loading={loading} />}

      {!loading && (
        <>
          <div className={styles["content-wrapper"]}>
            {!isCustomerAdmin && (
              <DeficiencyType type={type} subType={subType} />
            )}

            {isExterminatorResponsibility && (
              <TechniciansList technicians={technicians} />
            )}

            <TabTypeSelection
              readonly
              icon="accountDark"
              label="Verantwortung"
              tabs={["SBK", "Kunde"]}
              activeTab={activeTab}
              changeActiveTab={() => {}}
            />

            <div className={styles["map"]}>
              <CustomCollapseWithChildren
                active={!!propertyAreaMap}
                collapsedByDefault
                rightIcon="collapseArrow"
                label={t("Map")}
                defaultText={t("NoMapAvailable")}
              >
                <div className={styles["map-content"]}>
                  <PinCropMap
                    map={propertyAreaMap}
                    crop={
                      deficiency?.data.mapPosition?.mapPoint ?? { x: 1, y: 1 }
                    }
                    onCropChange={() => {}}
                    monitorPinAbbr={monitorPinAbbr}
                    isDraggable={false}
                    zoom={false}
                  />
                </div>
              </CustomCollapseWithChildren>
            </div>

            <CustomInput
              readonly
              label="Standortinformation"
              placeholder="Beschreibung"
              initialValue={deficiencyLocation}
              hideTick
            />

            <CustomInput
              readonly
              label="Empfehlungen"
              placeholder="Beschreibung"
              initialValue={deficiency?.data.requiredActions[0] ?? ""}
              hideTick
            />

            <ResourceImages images={images} />

            <CustomTextArea
              readonly
              placeholder="Zusätzlicher Text"
              onTextareaChange={() => {}}
              defaultValue={deficiency?.data.comment ?? ""}
            />

            {!isExterminatorResponsibility && (
              <Comments
                comments={comments}
                addComment={addComment}
                loading={loadingComments}
              />
            )}
          </div>

          {(isCustomerAdmin &&
            deficiency?.data.responsibility === "customer") ||
          (!isCustomerAdmin && !isViewOnly) ? (
            <FloatingButtons>
              <CustomButton
                color="green"
                icon="clock"
                iconPosition="left"
                width={80}
                onClick={handleFixDeficiency}
              >
                {t("FixDeficiency")}
              </CustomButton>
            </FloatingButtons>
          ) : null}
        </>
      )}
    </AppLayout>
  );
};

export default DeficiencyInsight;
